/**
 * Arabic Translations (العربية)
 * Note: Arabic is RTL (right-to-left)
 */

import type { Translations } from '../types';

export const ar: Translations = {
  language: 'ar',
  languageName: 'العربية',

  common: {
    demo: 'تجريبي',
    privacy: 'الخصوصية',
    terms: 'الشروط',
  },

  privacy: {
    title: 'سياسة الخصوصية',
    whatWeStore: 'ما نخزنه',
    whatWeStoreDesc:
      'نعالج هندسة الوجه مؤقتاً لتقدير العمر. لا يتم تخزين أي صور أو قوالب بيومترية بعد إتمام التحقق.',
    whatWeDont: 'ما لا نخزنه',
    whatWeDontDesc:
      'لا نخزن الصور أو مقاطع الفيديو أو المعرفات البيومترية. تتم معالجة بيانات وجهك في المتصفح وتُحذف فوراً.',
    whyWeUseIt: 'لماذا نستخدمه',
    whyWeUseItDesc:
      'يساعد تقدير العمر المواقع على الامتثال للوائح تقييد العمر دون الحاجة إلى وثائق شخصية.',
    readFull: 'اقرأ كامل',
    privacyPolicyLink: 'سياسة الخصوصية',
  },

  theme: {
    system: 'النظام',
    light: 'فاتح',
    dark: 'داكن',
  },

  face: {
    noFace: 'لم يتم اكتشاف وجه',
    tooFar: 'الوجه بعيد جداً',
    tooClose: 'الوجه قريب جداً',
    offCenter: 'الوجه غير مرتكز',
    poorLighting: 'إضاءة ضعيفة',
    good: 'جاري التحليل، انتظر...',
    holdStill: 'جاري التحليل، انتظر...',
    holdStillCountdown: 'ابق ثابتاً... {count}',
    processing: 'جاري المعالجة...',
    looking: 'لم يتم اكتشاف وجه',
    centerFace: 'الوجه غير مرتكز',
    moveCloser: 'الوجه بعيد جداً',
    moveBack: 'الوجه قريب جداً',
    keepInPosition: 'حافظ على الوضع',
    perfect: 'ممتاز',
    centerInFrame: 'ضع وجهك داخل الإطار',
    outsideOval: 'الوجه خارج البيضاوي',
  },

  liveness: {
    blink: {
      title: 'أغمض عينيك',
      hint: 'أغمض عينيك ببطء مرة واحدة',
    },
    smile: {
      title: 'ابتسم',
      hint: 'أظهر ابتسامة طبيعية',
    },
    lookLeft: {
      title: 'انظر لليسار',
      hint: 'أدر رأسك ببطء إلى اليسار',
    },
    lookRight: {
      title: 'انظر لليمين',
      hint: 'أدر رأسك ببطء إلى اليمين',
    },
    openMouth: {
      title: 'افتح فمك',
      hint: 'افتح فمك واسعاً',
    },
    lookUp: {
      title: 'انظر للأعلى',
      hint: 'ارفع رأسك للأعلى',
    },
    lookDown: {
      title: 'انظر للأسفل',
      hint: 'اخفض رأسك للأسفل',
    },
    raiseBrows: {
      title: 'ارفع حاجبيك',
      hint: 'ارفع حاجبيك',
    },
    improveLighting: {
      title: 'حسّن الإضاءة',
      hint: 'انتقل إلى مكان أكثر إضاءة للمتابعة',
    },
  },

  document: {
    selectType: 'اختر نوع المستند',
    captureFront: 'التقط الوجه الأمامي للمستند',
    captureBack: 'التقط الوجه الخلفي للمستند',
    processing: 'جاري معالجة المستند...',
    tips: {
      goodLighting: 'تأكد من وجود إضاءة جيدة',
      flatSurface: 'ضع المستند على سطح مستوٍ',
      allCorners: 'تأكد من ظهور جميع الزوايا',
      noGlare: 'تجنب الانعكاسات والظلال',
    },
    types: {
      passport: 'جواز السفر',
      idCard: 'بطاقة الهوية',
      driversLicense: 'رخصة القيادة',
    },
  },

  ui: {
    continue: 'متابعة',
    next: 'التالي',
    retry: 'حاول مرة أخرى',
    startOver: 'ابدأ من جديد',
    cancel: 'إلغاء',
    success: 'نجح فحص العمر',
    failure: 'فشل فحص العمر',
    loading: 'جاري التحميل...',
    error: 'حدث خطأ',
    permissionRequired: 'الإذن مطلوب',
    cameraPermission: 'مطلوب الوصول إلى الكاميرا',
    goBack: 'رجوع',
    switchCamera: 'تبديل الكاميرا',
    completeVerification: 'إكمال',
    continueToId: 'المتابعة إلى فحص الهوية',
  },

  errors: {
    cameraNotFound: 'لم يتم العثور على كاميرا في هذا الجهاز',
    cameraPermissionDenied: 'تم رفض إذن الكاميرا',
    cameraInUse: 'الكاميرا قيد الاستخدام من قبل تطبيق آخر',
    cameraNotSupported: 'الكاميرا لا تدعم الإعدادات المطلوبة',
    cameraSecurityError: 'الكاميرا تتطلب اتصال HTTPS',
    networkError: 'خطأ في الشبكة. يرجى التحقق من اتصالك.',
    timeout: 'انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.',
    invalidApiKey: 'مفتاح API غير صالح',
    sessionExpired: 'انتهت الجلسة. يرجى البدء من جديد.',
    verificationFailed: 'فشل الفحص. يرجى المحاولة مرة أخرى.',
    unknownError: 'حدث خطأ غير متوقع',
    cameraAccessDeniedDetailed: 'يرجى السماح بالوصول إلى الكاميرا للمتابعة.',
    cameraNotFoundDetailed: 'لم يتم اكتشاف كاميرا. يرجى توصيل كاميرا والمحاولة مرة أخرى.',
    cameraInUseDetailed: 'الكاميرا قيد الاستخدام من قبل تطبيق آخر. يرجى إغلاقه والمحاولة مرة أخرى.',
    cameraOverconstrained:
      'الكاميرا لا تدعم الدقة المطلوبة. جاري المحاولة بالإعدادات الافتراضية...',
    networkTimeout: 'استغرق الطلب وقتاً طويلاً. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
    networkOffline: 'يبدو أنك غير متصل بالإنترنت. يرجى التحقق من اتصالك.',
    apiGenericError: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    faceNotDetected: 'يرجى وضع وجهك في الإطار.',
    livenessFailed: 'فشل فحص الحيوية. يرجى المحاولة مرة أخرى.',
    documentCaptureFailed:
      'تعذر التقاط المستند. يرجى التأكد من وجود إضاءة جيدة والمحاولة مرة أخرى.',
    initializationFailed: 'فشل التحميل. يرجى تحديث الصفحة والمحاولة مرة أخرى.',
    modelLoadFailed: 'فشل تحميل المكونات المطلوبة. يرجى تحديث الصفحة والمحاولة مرة أخرى.',
    unexpectedError: 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.',
    configError: 'خطأ في التكوين. يرجى الاتصال بالدعم.',
  },

  screens: {
    welcome: {
      title: 'التحقق من العمر 18+',
      subtitle: 'فحص عمر مجهول الهوية',
      startButton: 'ابدأ',
      secureNote: 'آمن ومشفر',
      ageTechBy: 'تقنية العمر من AppBouncer',
      waitingCamera: 'في انتظار الوصول إلى الكاميرا',
      allowCamera: 'السماح بالوصول إلى الكاميرا',
      noBiometric: 'لا يتم تخزين بيانات بيومترية',
      loading: 'جاري التحميل...',
      loadingRequirements: 'جاري تحميل المتطلبات...',
    },
    permissions: {
      title: 'جاري التحميل ...',
      description: 'يرجى السماح بالوصول إلى الكاميرا عند المطالبة',
      allowButton: 'السماح بالوصول إلى الكاميرا',
      privacyNote: 'بياناتك مشفرة وآمنة',
      goodLighting: 'إضاءة جيدة!',
      goodLightingHint: 'تأكد من أن وجهك مضاء بشكل جيد',
      getReady: 'استعد',
      removeCoverings: 'أزل أغطية الوجه',
      removeGlasses: 'أزل النظارات',
    },
    facePosition: {
      title: 'ضع وجهك',
      hint: 'وسّط وجهك',
      step: 'الخطوة',
      stepOf: 'الخطوة {current} من {total}',
      lookingForFace: 'البحث عن الوجه...',
    },
    livenessCheck: {
      title: 'فحص الحيوية',
      followInstructions: 'اتبع التعليمات',
      preparing: 'جارٍ التحضير...',
      complete: 'تم التحقق من الحيوية!',
      actionAutoDetected: 'سيتم اكتشاف الإجراء تلقائياً',
      failed: 'فشل فحص الحيوية',
      failedDescription:
        'لم نتمكن من التحقق من حيويتك. يرجى التأكد من وجود إضاءة جيدة، والنظر مباشرة إلى الكاميرا، واتباع التعليمات الظاهرة على الشاشة.',
      retryButton: 'إعادة فحص الحيوية',
      restartButton: 'البدء من جديد',
    },
    ageEstimation: {
      title: 'ابق ثابتاً',
      analyzing: 'جاري التحليل...',
      analyzingProgress: 'تحليل الوجه {current} من {total}...',
      estimatedAge: 'العمر المقدر',
      holdStill: 'ابق ثابتاً',
      verified: 'تم التحقق من العمر',
      verificationUncertain: 'العمر غير مؤكد',
      estimationComplete: 'اكتمل فحص العمر',
      confirmedAge: 'مؤكد {minAge}+ سنة',
      couldNotConfirm: 'تعذر تأكيد العمر {minAge}+',
      ageConfirmed: 'تم تأكيد العمر',
      aboveMinimumAge: 'أنت تستوفي شرط الحد الأدنى للعمر',
      belowMinimumAge: 'تحت الحد الأدنى للعمر',
      documentRequired: 'مطلوب مستند',
      uploadDocumentMessage: 'يرجى تحميل وثيقة هوية صالحة للتحقق من عمرك',
      idHint: 'بطاقة هوية أو جواز سفر أو رخصة قيادة',
      confidenceLevels: 'الثقة حسب حد العمر',
      confidence: 'الثقة',
      years: 'سنة',
      continueToId: 'المتابعة إلى فحص الهوية',
      demoComplete: 'اكتمل الوضع التجريبي. في الإنتاج، ستتم إعادة توجيهك.',
      improveLighting: 'تحسين الإضاءة',
      improveLightingHint: 'انتقل إلى مكان أكثر إضاءة للحصول على نتائج دقيقة',
    },
    documentSelect: {
      title: 'اختر نوع المستند',
      description: 'اختر نوع وثيقة الهوية التي ستقوم بمسحها',
      idCard: 'بطاقة الهوية الوطنية',
      idCardHint: 'هوية صادرة من الحكومة',
      passport: 'جواز السفر',
      passportHint: 'صفحة الصورة فقط',
      driversLicense: 'رخصة القيادة',
      driversLicenseHint: 'رخصة قيادة سارية',
    },
    captureMode: {
      captureFrontSide: 'التقاط الواجهة الأمامية',
      captureBackSide: 'التقاط الواجهة الخلفية',
      chooseMethod: 'اختر طريقة التقاط المستند',
      useCamera: 'استخدام الكاميرا',
      useCameraHint: 'التقط صورة للمستند',
      uploadFile: 'تحميل ملف',
      uploadFileHint: 'اختر من جهازك',
    },
    documentCamera: {
      frontSide: 'الواجهة الأمامية',
      backSide: 'الواجهة الخلفية',
      positionInFrame: 'ضع المستند في الإطار',
      scanFront: 'مسح واجهة المستند الأمامية',
      scanBack: 'مسح واجهة المستند الخلفية',
      keepFlat: 'أبقِ المستند مسطحاً وبإضاءة جيدة',
      capturePhoto: 'التقاط الصورة',
    },
    documentPreview: {
      frontPreview: 'معاينة الواجهة الأمامية',
      backPreview: 'معاينة الواجهة الخلفية',
      lookingGood: 'يبدو جيداً!',
      checkDetails: 'تأكد من وضوح جميع التفاصيل',
      imageQuality: 'جودة الصورة',
      qualityGood: 'جيدة',
      usePhoto: 'استخدام هذه الصورة',
      retakePhoto: 'إعادة التقاط الصورة',
    },
    documentCapture: {
      captureFront: 'التقاط الواجهة الأمامية',
      captureBack: 'التقاط الواجهة الخلفية',
      alignDocument: 'قم بمحاذاة المستند داخل الإطار',
      captureButton: 'التقاط',
      retakeButton: 'إعادة الالتقاط',
      confirmButton: 'تأكيد',
      frontSide: 'الواجهة الأمامية',
      backSide: 'الواجهة الخلفية',
      positionDocument: 'ضع المستند في الإطار',
      alignWithFrame: 'قم بمحاذاة المستند مع الإطار',
      positionFrontId: 'ضع واجهة الهوية الأمامية',
      positionBackId: 'ضع واجهة الهوية الخلفية',
      scanFrontId: 'مسح واجهة الهوية الأمامية',
      scanBackId: 'مسح واجهة الهوية الخلفية',
      positionWithinFrame: 'ضع الواجهة الأمامية داخل الإطار',
      flipAndScan: 'اقلب الهوية وامسح الواجهة الخلفية',
      goodLight: 'إضاءة جيدة',
      flatSurface: 'سطح مستوٍ',
      frontCaptured: 'تم التقاط الواجهة الأمامية',
      captureIdCard: 'التقاط بطاقة الهوية',
      capturePassport: 'التقاط جواز السفر',
      captureDriversLicense: 'التقاط رخصة القيادة',
    },
    processing: {
      title: 'جارٍ المعالجة',
      description: 'يرجى الانتظار بينما نعالج',
      analyzingDocument: 'جاري تحليل المستند...',
      almostDone: 'على وشك الانتهاء...',
      faceVerified: 'تم التحقق من الوجه',
      livenessConfirmed: 'تم تأكيد الحيوية',
      processingDocuments: 'جاري معالجة المستندات...',
    },
    success: {
      title: 'تم اجتياز التحقق من العمر',
      description: 'اكتمل التحقق من العمر والحيوية',
      redirecting: 'جاري إعادة التوجيه...',
      summary: 'الملخص',
      faceMatch: 'مطابقة الوجه',
      liveness: 'الحيوية',
      age: 'العمر',
      idDocument: 'وثيقة الهوية',
      pass: 'ناجح',
      fail: 'فاشل',
      valid: 'صالح',
      notApplicable: 'غير متاح',
      done: 'تم',
      feedbackTitle: 'يرجى تقييم تجربتك',
      feedbackPlaceholder: 'أخبرنا كيف يمكننا التحسين...',
      skip: 'تخطي',
      submit: 'إرسال',
    },
    failure: {
      title: 'فشل فحص العمر',
      description: 'لم نتمكن من إكمال الفحص. يرجى المحاولة مرة أخرى.',
      tryAgainButton: 'حاول مرة أخرى',
      leaveButton: 'مغادرة',
      attemptsLeft: '{{count}} محاولة(ات) متبقية',
      noAttemptsLeft: 'لا توجد محاولات متبقية',
    },
    error: {
      title: 'التكوين مطلوب',
      description: 'بعض المعلمات المطلوبة مفقودة من الرابط. يرجى الاتصال بمسؤول الموقع.',
      missingParams: 'معلمات مفقودة',
      requiredParams: 'المعلمات المطلوبة',
      technicalDetails: 'التفاصيل التقنية',
      urlMustInclude: 'يجب أن يتضمن الرابط معلمات الاستعلام التالية:',
    },
    underage: {
      title: 'لم يتم استيفاء شرط العمر',
      description: 'يجب أن يكون عمرك {{minAge}} عامًا على الأقل للوصول إلى هذه الخدمة.',
    },
    consent: {
      title: 'ساعد في تحسين فحص العمر',
      description:
        'اسمح لـ AppBouncer بجمع كمية صغيرة من بيانات الاستخدام المجهولة (مثل التقاط الكاميرا) لتحسين التحقق من العمر والتحقق الاحتياطي. لن يتم بياناتك أو تقديمها لأطراف ثالثة أبداً.',
      allowButton: 'السماح',
    },
    documentConsent: {
      title: 'مستند مطلوب',
      description:
        'لإكمال الفحص، نحتاج إلى التحقق من عمرك باستخدام مستند رسمي. هل توافق على تحميل وثيقة هويتك؟',
      allowButton: 'المتابعة مع المستند',
      declineButton: 'رفض',
      privacyNote: 'سيتم معالجة مستندك بشكل آمن وحذفه بعد الفحص.',
    },
    selfDeclaration: {
      title: 'تاريخ ميلادك',
      description: 'يرجى إدخال تاريخ ميلادك للمتابعة',
      day: 'اليوم',
      month: 'الشهر',
      year: 'السنة',
      yourAge: 'عمرك:',
      years: 'سنة',
      confirmButton: 'متابعة',
      months: {
        january: 'يناير',
        february: 'فبراير',
        march: 'مارس',
        april: 'أبريل',
        may: 'مايو',
        june: 'يونيو',
        july: 'يوليو',
        august: 'أغسطس',
        september: 'سبتمبر',
        october: 'أكتوبر',
        november: 'نوفمبر',
        december: 'ديسمبر',
      },
    },
  },

  lighting: {
    warning: 'مشكلة في الإضاءة',
    adjust: 'اضبط الإضاءة',
    lowLight: 'تم اكتشاف إضاءة منخفضة',
    tooBright: 'ساطع جداً',
    poorContrast: 'تباين ضعيف',
    improveLighting: 'انتقل إلى إضاءة أفضل',
    moveToLighter: 'انتقل إلى مكان أكثر إضاءة',
    videoNotReady: 'الفيديو غير جاهز',
  },

  steps: {
    faceDetection: 'التعرف على الوجه',
    liveness: 'التحقق من الحياة',
    age: 'فحص العمر',
    documentScan: 'مسح المستند',
    selfDeclaration: 'إقرار ذاتي',
  },

  status: {
    cameraStarted: 'تم تشغيل الكاميرا',
    cameraReady: 'الكاميرا جاهزة',
    cameraAccessDenied: 'تم رفض الوصول إلى الكاميرا',
    cameraError: 'خطأ في الكاميرا',
    cameraNotAvailable: 'الكاميرا غير متاحة',
    noFaceDetected: 'لم يتم اكتشاف وجه',
    livenessVerified: 'تم التحقق من الحيوية',
    livenessCheckFailed: 'فشل فحص الحيوية',
    verifyingAge: 'جاري التحقق من العمر...',
    processingDocument: 'جاري معالجة المستند...',
    documentProcessed: 'تمت معالجة المستند بنجاح',
    verificationCancelled: 'تم إلغاء الفحص',
    improveLightingForAge: 'حسّن الإضاءة للحصول على نتائج دقيقة',
    getReady: 'استعد',
    preparingLiveness: 'جاري إعداد فحص الحيوية...',
    uploadingSelfie: 'جاري رفع صور السيلفي...',
    preparingUpload: 'جاري إعداد الرفع...',
    uploadingFront: 'جاري رفع المستند الأمامي...',
    uploadingBack: 'جاري رفع المستند الخلفي...',
    processingDocuments: 'جاري معالجة المستندات...',
    documentSubmissionFailed: 'فشل إرسال المستند. يرجى المحاولة مرة أخرى.',
    selectJpegOrPng: 'يرجى اختيار صورة JPEG أو PNG',
    fileTooLarge: 'الملف كبير جداً. الحد الأقصى 5 ميغابايت.',
    errorReadingFile: 'خطأ في قراءة الملف',
    frontDocumentRequired: 'صورة المستند الأمامي مطلوبة',
    verificationFailedClose: 'فشل الفحص. سيتم إعادة توجيهك.',
    verificationCompleteClose: 'اكتمل الفحص! سيتم إعادة توجيهك.',
    analyzingFace: 'جاري تحليل الوجه...',
  },
};
